import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Space, Spin, message } from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import DataVisualization from '../common/DataVisualization';
import ReportService from '../../services/reportBuilder.service';

const { Option } = Select;

const ChartWidget = ({
  widget,
  onEdit,
  onRefresh,
  isEditing = false,
  style = {}
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState(widget?.config?.chartType || 'bar');
  const [xAxisField, setXAxisField] = useState(widget?.config?.xAxisField);
  const [yAxisField, setYAxisField] = useState(widget?.config?.yAxisField);

  useEffect(() => {
    if (widget?.config?.dataSource) {
      loadWidgetData();
    }
  }, [widget]);

  const loadWidgetData = async () => {
    if (!widget?.config?.dataSource) return;

    setLoading(true);
    try {
      const result = await ReportService.executeSavedSearch(
        widget.config.entityType || 'items',
        widget.config.dataSource,
        1000, // limit
        0 // offset
      );

      if (result && result.data) {
        setData(result.data);
      }
    } catch (error) {
      console.error('Error loading widget data:', error);
      message.error('Failed to load widget data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadWidgetData();
    onRefresh && onRefresh(widget.id);
  };

  const handleChartTypeChange = (value) => {
    setChartType(value);
    // Update widget config
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          chartType: value
        }
      });
    }
  };

  const handleAxisChange = (axis, value) => {
    if (axis === 'x') {
      setXAxisField(value);
    } else {
      setYAxisField(value);
    }

    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          [axis === 'x' ? 'xAxisField' : 'yAxisField']: value
        }
      });
    }
  };

  // Get available fields from data
  const availableFields = data.length > 0 ? Object.keys(data[0]) : [];

  return (
    <Card
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{widget?.name || 'Chart Widget'}</span>
          <Space>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              size="small"
            />
            {isEditing && (
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => onEdit && onEdit(widget.id)}
                size="small"
              />
            )}
          </Space>
        </Space>
      }
      style={{
        height: widget?.config?.size?.height || 400,
        ...style
      }}
      bodyStyle={{ height: 'calc(100% - 60px)', padding: '16px' }}
    >
      {isEditing ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={chartType}
            onChange={handleChartTypeChange}
            style={{ width: '100%' }}
            placeholder="Select chart type"
          >
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
            <Option value="pie">Pie Chart</Option>
          </Select>

          <Select
            value={xAxisField}
            onChange={(value) => handleAxisChange('x', value)}
            style={{ width: '100%' }}
            placeholder="Select X-axis field"
          >
            {availableFields.map(field => (
              <Option key={field} value={field}>
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Option>
            ))}
          </Select>

          <Select
            value={yAxisField}
            onChange={(value) => handleAxisChange('y', value)}
            style={{ width: '100%' }}
            placeholder="Select Y-axis field"
          >
            {availableFields.map(field => (
              <Option key={field} value={field}>
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Option>
            ))}
          </Select>
        </Space>
      ) : (
        <Spin spinning={loading}>
          <DataVisualization
            data={data}
            type={chartType}
            xAxisField={xAxisField}
            yAxisField={yAxisField}
            title={widget?.name || 'Chart'}
            height="100%"
          />
        </Spin>
      )}
    </Card>
  );
};

export default ChartWidget;